/**
 * MapView2D - 2D map using MapLibre GL JS
 * Renders order polygon with OSM/Satellite basemap
 */

import { useRef, useEffect, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { useAtlasStore } from '@/stores/atlasStore';
import { OSM_PMTILES_URL } from '@/services/api';

const BASEMAPS: Record<string, { url: string; attribution: string }> = {
  osm: {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri',
  },
  terrain: {
    url: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenTopoMap contributors',
  },
};

export function MapView2D() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const polygonAddedRef = useRef(false);

  const orderPolygon = useAtlasStore((s) => s.orderPolygon);
  const orderBounds = useAtlasStore((s) => s.orderBounds);
  const showPolygon = useAtlasStore((s) => s.showPolygon);
  const baseLayer = useAtlasStore((s) => s.baseLayer);
  const viewMode = useAtlasStore((s) => s.viewMode);
  const setMapCenter = useAtlasStore((s) => s.setMapCenter);
  const setMapZoom = useAtlasStore((s) => s.setMapZoom);
  const mapCenter = useAtlasStore((s) => s.mapCenter);
  const mapZoom = useAtlasStore((s) => s.mapZoom);
  // Terreno Base raster layers (standard products, LOLA/DGT provider)
  const mdtVisible = useAtlasStore((s) => s.mdtVisible);
  const mdtOpacity = useAtlasStore((s) => s.mdtOpacity);
  const hillshadeVisible = useAtlasStore((s) => s.hillshadeVisible);
  const hillshadeOpacity = useAtlasStore((s) => s.hillshadeOpacity);
  const hillshadeHypsoVisible = useAtlasStore((s) => s.hillshadeHypsoVisible);
  const hillshadeHypsoOpacity = useAtlasStore((s) => s.hillshadeHypsoOpacity);
  // Morfologia
  const slopeVisible = useAtlasStore((s) => s.slopeVisible);
  const slopeOpacity = useAtlasStore((s) => s.slopeOpacity);
  const aspectVisible = useAtlasStore((s) => s.aspectVisible);
  const aspectOpacity = useAtlasStore((s) => s.aspectOpacity);
  const geomorphonsVisible = useAtlasStore((s) => s.geomorphonsVisible);
  const geomorphonsOpacity = useAtlasStore((s) => s.geomorphonsOpacity);
  const windExpositionVisible = useAtlasStore((s) => s.windExpositionVisible);
  const windExpositionOpacity = useAtlasStore((s) => s.windExpositionOpacity);
  // Contour layer (vector)
  const contoursVisible = useAtlasStore((s) => s.contoursVisible);
  const contoursOpacity = useAtlasStore((s) => s.contoursOpacity);
  // Flow Accumulation (raster)
  const flowAccVisible = useAtlasStore((s) => s.flowAccVisible);
  const flowAccOpacity = useAtlasStore((s) => s.flowAccOpacity);
  // Streams layer (vector)
  const streamsVisible = useAtlasStore((s) => s.streamsVisible);
  const streamsOpacity = useAtlasStore((s) => s.streamsOpacity);
  // Ridges layer (vector)
  const ridgesVisible = useAtlasStore((s) => s.ridgesVisible);
  const ridgesOpacity = useAtlasStore((s) => s.ridgesOpacity);
  // Basins layer (vector polygons)
  const basinsVisible = useAtlasStore((s) => s.basinsVisible);
  const basinsOpacity = useAtlasStore((s) => s.basinsOpacity);
  // Primary valleys (vector polygons)
  const primaryValleysVisible = useAtlasStore((s) => s.primaryValleysVisible);
  const primaryValleysOpacity = useAtlasStore((s) => s.primaryValleysOpacity);
  // Primary ridges (vector polygons)
  const primaryRidgesVisible = useAtlasStore((s) => s.primaryRidgesVisible);
  const primaryRidgesOpacity = useAtlasStore((s) => s.primaryRidgesOpacity);
  // Erosion risk (raster)
  const erosionRiskVisible = useAtlasStore((s) => s.erosionRiskVisible);
  const erosionRiskOpacity = useAtlasStore((s) => s.erosionRiskOpacity);
  // Keypoints (vector points)
  const keypointsVisible = useAtlasStore((s) => s.keypointsVisible);
  const keypointsOpacity = useAtlasStore((s) => s.keypointsOpacity);
  // Flood zones layer (vector polygons)
  const floodZonesVisible = useAtlasStore((s) => s.floodZonesVisible);
  const floodZonesOpacity = useAtlasStore((s) => s.floodZonesOpacity);
  // OSM Context — Roads & Buildings
  const roadsVisible = useAtlasStore((s) => s.roadsVisible);
  const roadsOpacity = useAtlasStore((s) => s.roadsOpacity);
  const buildingsVisible = useAtlasStore((s) => s.buildingsVisible);
  const buildingsOpacity = useAtlasStore((s) => s.buildingsOpacity);
  const landuseVisible = useAtlasStore((s) => s.landuseVisible);
  const landuseOpacity = useAtlasStore((s) => s.landuseOpacity);
  const waterwaysVisible = useAtlasStore((s) => s.waterwaysVisible);
  const waterwaysOpacity = useAtlasStore((s) => s.waterwaysOpacity);
  const adminBoundsVisible = useAtlasStore((s) => s.adminBoundsVisible);
  const adminBoundsOpacity = useAtlasStore((s) => s.adminBoundsOpacity);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const basemap = BASEMAPS[baseLayer] || BASEMAPS.osm;

    // Register PMTiles protocol for OSM context layers
    const pmtilesProtocol = new Protocol();
    maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile);

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
        sources: {
          basemap: {
            type: 'raster',
            tiles: [basemap.url],
            tileSize: 256,
            attribution: basemap.attribution,
          },
        },
        layers: [
          {
            id: 'basemap-layer',
            type: 'raster',
            source: 'basemap',
          },
        ],
      },
      center: mapCenter,
      zoom: mapZoom,
    });

    map.addControl(new maplibregl.NavigationControl(), 'bottom-right');
    map.addControl(new maplibregl.ScaleControl(), 'bottom-right');

    // Sync camera to store on move
    map.on('moveend', () => {
      const center = map.getCenter();
      setMapCenter([center.lng, center.lat]);
      setMapZoom(map.getZoom());
    });

    mapRef.current = map;
    (window as any).__maplibreMap = map;

    // Subscribe to Zustand store to add raster layers as tile URLs arrive
    // Standard products (LOLA/DGT provider produces high-res output)
    const RASTER_LAYERS = [
      { id: 'mdt-layer', urlKey: 'mdtTileUrl' as const, visKey: 'mdtVisible' as const, opKey: 'mdtOpacity' as const, zoomKey: 'mdtZoomRange' as const },
      { id: 'hillshade-layer', urlKey: 'hillshadeTileUrl' as const, visKey: 'hillshadeVisible' as const, opKey: 'hillshadeOpacity' as const, zoomKey: 'hillshadeZoomRange' as const },
      { id: 'hillshade-hypso-layer', urlKey: 'hillshadeHypsoTileUrl' as const, visKey: 'hillshadeHypsoVisible' as const, opKey: 'hillshadeHypsoOpacity' as const, zoomKey: 'hillshadeHypsoZoomRange' as const },
      { id: 'slope-layer', urlKey: 'slopeTileUrl' as const, visKey: 'slopeVisible' as const, opKey: 'slopeOpacity' as const, zoomKey: 'slopeZoomRange' as const },
      { id: 'aspect-layer', urlKey: 'aspectTileUrl' as const, visKey: 'aspectVisible' as const, opKey: 'aspectOpacity' as const, zoomKey: 'aspectZoomRange' as const },
      { id: 'geomorphons-layer', urlKey: 'geomorphonsTileUrl' as const, visKey: 'geomorphonsVisible' as const, opKey: 'geomorphonsOpacity' as const, zoomKey: 'geomorphonsZoomRange' as const },
      { id: 'wind-hillshade-layer', urlKey: 'windHillshadeTileUrl' as const, visKey: 'windExpositionVisible' as const, opKey: 'windExpositionOpacity' as const, zoomKey: 'windHillshadeZoomRange' as const },
      { id: 'wind-exposition-layer', urlKey: 'windExpositionTileUrl' as const, visKey: 'windExpositionVisible' as const, opKey: 'windExpositionOpacity' as const, zoomKey: 'windExpositionZoomRange' as const },
      { id: 'erosion-risk-layer', urlKey: 'erosionRiskTileUrl' as const, visKey: 'erosionRiskVisible' as const, opKey: 'erosionRiskOpacity' as const, zoomKey: 'erosionRiskZoomRange' as const },
      { id: 'flow-acc-layer', urlKey: 'flowAccTileUrl' as const, visKey: 'flowAccVisible' as const, opKey: 'flowAccOpacity' as const, zoomKey: 'flowAccZoomRange' as const },
    ];

    const addMissingLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      for (const layer of RASTER_LAYERS) {
        const tileUrl = state[layer.urlKey];
        if (!tileUrl || map.getSource(layer.id)) continue;
        const zoomRange = layer.zoomKey ? (state as any)[layer.zoomKey] : null;
        const sourceOpts: any = { type: 'raster', tiles: [tileUrl], tileSize: 256 };
        if (zoomRange) {
          sourceOpts.minzoom = zoomRange.minzoom;
          sourceOpts.maxzoom = zoomRange.maxzoom;
        }
        if (state.orderBounds) {
          sourceOpts.bounds = state.orderBounds;
        }
        map.addSource(layer.id, sourceOpts);
        // Wind hillshade must be BELOW wind exposition — insert before it if it exists
        let beforeLayer: string | undefined;
        if (layer.id === 'wind-hillshade-layer' && map.getLayer('wind-exposition-layer')) {
          beforeLayer = 'wind-exposition-layer';
        } else {
          beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
        }
        map.addLayer({
          id: layer.id, type: 'raster', source: layer.id,
          paint: { 'raster-opacity': state[layer.visKey] ? state[layer.opKey] : 0 },
        }, beforeLayer);
        console.log(`${layer.id} added to 2D map`);
      }
      // Ensure wind hillshade is always below wind exposition (may arrive in wrong order)
      if (map.getLayer('wind-hillshade-layer') && map.getLayer('wind-exposition-layer')) {
        const layers = map.getStyle().layers.map((l: any) => l.id);
        if (layers.indexOf('wind-hillshade-layer') > layers.indexOf('wind-exposition-layer')) {
          map.moveLayer('wind-hillshade-layer', 'wind-exposition-layer');
        }
      }
    };

    // Add DGT contour vector layers when tile URL arrives
    const addContourDgtLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.contoursTileUrl || !state.contoursSourceLayer || !state.contoursItemId) return;
      if (map.getLayer('contours-dgt-5m-layer')) return;

      if (!map.getSource('contours-dgt')) map.addSource('contours-dgt', {
        type: 'vector',
        tiles: [state.contoursTileUrl],
        minzoom: 0,
        maxzoom: 16,  // Overzoom: reuse zoom-16 tiles at lower zooms to preserve 50cm detail
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemId = state.contoursItemId;

      // 5m contour lines — black per Altimetria.qml spec
      map.addLayer({
        id: 'contours-dgt-5m-layer',
        type: 'line',
        source: 'contours-dgt',
        'source-layer': state.contoursSourceLayer,
        filter: ['all',
          ['==', ['to-string', ['get', 'order_item_id']], itemId],
          ['==', ['%', ['get', 'elevation'], 5], 0],
        ],
        paint: {
          'line-color': '#000000',
          'line-width': 1.2,
          'line-opacity': state.contoursVisible ? state.contoursOpacity : 0,
        },
      }, beforeLayer);

      // 1m contour lines — visible at close zoom
      map.addLayer({
        id: 'contours-dgt-1m-layer',
        type: 'line',
        source: 'contours-dgt',
        'source-layer': state.contoursSourceLayer,
        filter: ['all',
          ['==', ['to-string', ['get', 'order_item_id']], itemId],
          ['==', ['%', ['get', 'elevation'], 1], 0],
          ['!=', ['%', ['get', 'elevation'], 5], 0],
        ],
        minzoom: 14,
        paint: {
          'line-color': '#000000',
          'line-width': 0.6,
          'line-opacity': state.contoursVisible ? state.contoursOpacity : 0,
        },
      }, beforeLayer);

      // Elevation labels on 5m contours (per Altimetria.qml: black, 6pt Open Sans, mask 1.5mm)
      map.addLayer({
        id: 'contours-dgt-labels-layer',
        type: 'symbol',
        source: 'contours-dgt',
        'source-layer': state.contoursSourceLayer,
        filter: ['all',
          ['==', ['to-string', ['get', 'order_item_id']], itemId],
          ['==', ['%', ['get', 'elevation'], 10], 0],
        ],
        layout: {
          'symbol-placement': 'point',
          'text-field': ['to-string', ['get', 'elevation']],
          'text-font': ['Noto Sans Regular'],
          'text-size': 8,
          'text-allow-overlap': false,
          visibility: state.contoursVisible ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#fafafa',
          'text-halo-width': 1.5,
          'text-opacity': 1,
        },
      });

      console.log('Contour DGT vector layers added to 2D map');
    };

    // Add streams vector layers (SAGA channel network, categorized by Strahler order)
    const addStreamsLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.streamsTileUrl || !state.streamsSourceLayer || !state.streamsItemId) return;
      if (map.getLayer('streams-order-1')) return;

      if (!map.getSource('streams-src')) map.addSource('streams-src', {
        type: 'vector',
        tiles: [state.streamsTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemId = state.streamsItemId;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], itemId];

      // Stream order layers — all visible for parcel-level analysis
      // Higher order = thicker, darker blue (QML colors)
      const streamStyles = [
        { id: 'streams-order-6', order: 6, color: '#08306b', width: 4.0 },
        { id: 'streams-order-5', order: 5, color: '#1f539f', width: 3.2 },
        { id: 'streams-order-4', order: 4, color: '#1f539f', width: 2.5 },
        { id: 'streams-order-3', order: 3, color: '#41718e', width: 2.0 },
        { id: 'streams-order-2', order: 2, color: '#5390b4', width: 1.5 },
        { id: 'streams-order-1', order: 1, color: '#74a9cf', width: 1.0 },
      ];

      for (const s of streamStyles) {
        map.addLayer({
          id: s.id,
          type: 'line',
          source: 'streams-src',
          'source-layer': state.streamsSourceLayer,
          filter: ['all', itemFilter, ['==', ['get', 'stream_order'], s.order]],
          paint: {
            'line-color': s.color,
            'line-width': s.width,
            'line-opacity': state.streamsVisible ? state.streamsOpacity : 0,
          },
        }, beforeLayer);
      }

      // Catch-all for orders >= 7 (if any)
      map.addLayer({
        id: 'streams-order-high',
        type: 'line',
        source: 'streams-src',
        'source-layer': state.streamsSourceLayer,
        filter: ['all', itemFilter, ['>=', ['get', 'stream_order'], 7]],
        paint: {
          'line-color': '#08306b',
          'line-width': 5.0,
          'line-opacity': state.streamsVisible ? state.streamsOpacity : 0,
        },
      }, beforeLayer);

      console.log('Streams vector layers added to 2D map');
    };

    // Add ridges vector layers (SAGA channel network on inverted DTM)
    const addRidgesLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.ridgesTileUrl || !state.ridgesSourceLayer || !state.ridgesItemId) return;
      if (map.getLayer('ridges-order-1')) return;

      if (!map.getSource('ridges-src')) map.addSource('ridges-src', {
        type: 'vector',
        tiles: [state.ridgesTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemId = state.ridgesItemId;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], itemId];

      // Ridge order layers — red/brown per QML (symmetric pair of blue streams)
      const ridgeStyles = [
        { id: 'ridges-order-6', order: 6, color: '#ba1419', width: 4.0 },
        { id: 'ridges-order-5', order: 5, color: '#ba3214', width: 3.0 },
        { id: 'ridges-order-4', order: 4, color: '#ba3214', width: 2.0 },
        { id: 'ridges-order-3', order: 3, color: '#ba3214', width: 1.5 },
        { id: 'ridges-order-2', order: 2, color: '#c43c39', width: 1.2 },
        { id: 'ridges-order-1', order: 1, color: '#c43c39', width: 0.8 },
      ];

      for (const s of ridgeStyles) {
        map.addLayer({
          id: s.id,
          type: 'line',
          source: 'ridges-src',
          'source-layer': state.ridgesSourceLayer,
          filter: ['all', itemFilter, ['==', ['get', 'stream_order'], s.order]],
          paint: {
            'line-color': s.color,
            'line-width': s.width,
            'line-opacity': state.ridgesVisible ? state.ridgesOpacity : 0,
          },
        }, beforeLayer);
      }

      map.addLayer({
        id: 'ridges-order-high',
        type: 'line',
        source: 'ridges-src',
        'source-layer': state.ridgesSourceLayer,
        filter: ['all', itemFilter, ['>=', ['get', 'stream_order'], 7]],
        paint: {
          'line-color': '#ba1419',
          'line-width': 5.0,
          'line-opacity': state.ridgesVisible ? state.ridgesOpacity : 0,
        },
      }, beforeLayer);

      console.log('Ridges vector layers added to 2D map');
    };

    // Add basins fill + outline + label layers (polygon vector)
    const addBasinsLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.basinsTileUrl || !state.basinsSourceLayer || !state.basinsItemId) return;
      if (map.getLayer('basins-fill')) return;

      if (!map.getSource('basins-src')) map.addSource('basins-src', {
        type: 'vector',
        tiles: [state.basinsTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemId = state.basinsItemId;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], itemId];

      // Pastel fill — color by label prefix (A=pink, B=teal, C=green, D=purple)
      map.addLayer({
        id: 'basins-fill',
        type: 'fill',
        source: 'basins-src',
        'source-layer': state.basinsSourceLayer,
        filter: itemFilter,
        paint: {
          'fill-color': ['match', ['slice', ['get', 'label'], 0, 1],
            'A', '#E8A4C8',
            'B', '#7ECFC0',
            'C', '#B5D46A',
            'D', '#9B8EC4',
            '#D4B896',
          ],
          'fill-opacity': state.basinsVisible ? state.basinsOpacity : 0,
        },
      }, beforeLayer);

      // Outline
      map.addLayer({
        id: 'basins-outline',
        type: 'line',
        source: 'basins-src',
        'source-layer': state.basinsSourceLayer,
        filter: itemFilter,
        paint: {
          'line-color': '#a31d98',
          'line-width': 1.5,
          'line-opacity': state.basinsVisible ? 1 : 0,
        },
      }, beforeLayer);

      // Labels (basin label + area)
      map.addLayer({
        id: 'basins-labels',
        type: 'symbol',
        source: 'basins-src',
        'source-layer': state.basinsSourceLayer,
        filter: itemFilter,
        layout: {
          'symbol-placement': 'point',
          'text-field': ['concat', ['get', 'label'], '\n', ['to-string', ['round', ['get', 'area_ha']]], ' Ha'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 11,
          'text-allow-overlap': false,
          visibility: state.basinsVisible ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#4a1048',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      });

      console.log('Basins vector layers added to 2D map');
    };

    // Add flood zones fill + outline + label layers (GFI-based risk polygons)
    const addFloodZonesLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.floodZonesTileUrl || !state.floodZonesSourceLayer || !state.floodZonesItemId) return;
      if (map.getLayer('flood-zones-fill')) return;

      if (!map.getSource('flood-zones-src')) map.addSource('flood-zones-src', {
        type: 'vector',
        tiles: [state.floodZonesTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], state.floodZonesItemId];

      // Fill — red/orange/yellow by risk class
      map.addLayer({
        id: 'flood-zones-fill',
        type: 'fill',
        source: 'flood-zones-src',
        'source-layer': state.floodZonesSourceLayer,
        filter: itemFilter,
        paint: {
          'fill-color': ['match', ['get', 'risk_class'],
            'high', '#d73027',
            'medium', '#fc8d59',
            'low', '#fee08b',
            '#cccccc',
          ],
          'fill-opacity': state.floodZonesVisible ? state.floodZonesOpacity : 0,
        },
      }, beforeLayer);

      // Outline
      map.addLayer({
        id: 'flood-zones-outline',
        type: 'line',
        source: 'flood-zones-src',
        'source-layer': state.floodZonesSourceLayer,
        filter: itemFilter,
        paint: {
          'line-color': ['match', ['get', 'risk_class'],
            'high', '#a50026',
            'medium', '#d73027',
            'low', '#f46d43',
            '#999999',
          ],
          'line-width': 1.5,
          'line-opacity': state.floodZonesVisible ? 1 : 0,
        },
      }, beforeLayer);

      // Labels
      map.addLayer({
        id: 'flood-zones-labels',
        type: 'symbol',
        source: 'flood-zones-src',
        'source-layer': state.floodZonesSourceLayer,
        filter: itemFilter,
        layout: {
          'symbol-placement': 'point',
          'text-field': ['concat', ['get', 'risk_label'], '\n', ['to-string', ['round', ['get', 'area_ha']]], ' Ha'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-allow-overlap': false,
          visibility: state.floodZonesVisible ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#7f0000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      });

      console.log('Flood zones vector layers added to 2D map');
    };

    // Add keypoints circle + label layers (valley inflection points)
    const addKeypointsLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.keypointsTileUrl || !state.keypointsSourceLayer || !state.keypointsItemId) return;
      if (map.getLayer('keypoints-circle')) return;

      if (!map.getSource('keypoints-src')) map.addSource('keypoints-src', {
        type: 'vector',
        tiles: [state.keypointsTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], state.keypointsItemId];

      // Gold circles with white halo
      map.addLayer({
        id: 'keypoints-circle',
        type: 'circle',
        source: 'keypoints-src',
        'source-layer': state.keypointsSourceLayer,
        filter: itemFilter,
        paint: {
          'circle-radius': 6,
          'circle-color': '#D4A843',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
          'circle-opacity': state.keypointsVisible ? state.keypointsOpacity : 0,
          'circle-stroke-opacity': state.keypointsVisible ? state.keypointsOpacity : 0,
        },
      });

      // Elevation labels
      map.addLayer({
        id: 'keypoints-labels',
        type: 'symbol',
        source: 'keypoints-src',
        'source-layer': state.keypointsSourceLayer,
        filter: itemFilter,
        layout: {
          'text-field': ['concat', ['to-string', ['round', ['get', 'elevation']]], 'm'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-offset': [0, 1.5],
          'text-allow-overlap': false,
          visibility: state.keypointsVisible ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#8B6914',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      });

      console.log('Keypoints vector layers added to 2D map');
    };

    // Add primary valleys fill + outline layers (geomorphon class 9 polygons)
    const addPrimaryValleysLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.primaryValleysTileUrl || !state.primaryValleysSourceLayer || !state.primaryValleysItemId) return;
      if (map.getLayer('primary-valleys-fill')) return;

      if (!map.getSource('primary-valleys-src')) map.addSource('primary-valleys-src', {
        type: 'vector',
        tiles: [state.primaryValleysTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], state.primaryValleysItemId];

      map.addLayer({
        id: 'primary-valleys-fill',
        type: 'fill',
        source: 'primary-valleys-src',
        'source-layer': state.primaryValleysSourceLayer,
        filter: itemFilter,
        paint: {
          'fill-color': '#698195',
          'fill-opacity': state.primaryValleysVisible ? state.primaryValleysOpacity : 0,
        },
      }, beforeLayer);

      map.addLayer({
        id: 'primary-valleys-outline',
        type: 'line',
        source: 'primary-valleys-src',
        'source-layer': state.primaryValleysSourceLayer,
        filter: itemFilter,
        paint: {
          'line-color': '#4a6070',
          'line-width': 1,
          'line-opacity': state.primaryValleysVisible ? 1 : 0,
        },
      }, beforeLayer);

      console.log('Primary valleys vector layers added to 2D map');
    };

    // Add primary ridges fill + outline layers (geomorphon class 3 polygons)
    const addPrimaryRidgesLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.primaryRidgesTileUrl || !state.primaryRidgesSourceLayer || !state.primaryRidgesItemId) return;
      if (map.getLayer('primary-ridges-fill')) return;

      if (!map.getSource('primary-ridges-src')) map.addSource('primary-ridges-src', {
        type: 'vector',
        tiles: [state.primaryRidgesTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], state.primaryRidgesItemId];

      map.addLayer({
        id: 'primary-ridges-fill',
        type: 'fill',
        source: 'primary-ridges-src',
        'source-layer': state.primaryRidgesSourceLayer,
        filter: itemFilter,
        paint: {
          'fill-color': '#ba2231',
          'fill-opacity': state.primaryRidgesVisible ? state.primaryRidgesOpacity : 0,
        },
      }, beforeLayer);

      map.addLayer({
        id: 'primary-ridges-outline',
        type: 'line',
        source: 'primary-ridges-src',
        'source-layer': state.primaryRidgesSourceLayer,
        filter: itemFilter,
        paint: {
          'line-color': '#8a1a25',
          'line-width': 1,
          'line-opacity': state.primaryRidgesVisible ? 1 : 0,
        },
      }, beforeLayer);

      console.log('Primary ridges vector layers added to 2D map');
    };

    // Add OSM roads context layer (PMTiles, always available, below analysis layers)
    const addRoadsContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-roads-motorway')) return;

      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', {
          type: 'vector',
          url: OSM_PMTILES_URL,
        });
      }

      const state = useAtlasStore.getState();

      // Insert roads above basemap but below all analysis layers
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') {
          insertBefore = l.id;
          break;
        }
      }

      const roadStyles = [
        { id: 'osm-roads-motorway', filter: ['motorway', 'motorway_link'], color: '#333333', width: [3, 6] },
        { id: 'osm-roads-trunk', filter: ['trunk', 'trunk_link'], color: '#444444', width: [2.5, 5] },
        { id: 'osm-roads-primary', filter: ['primary', 'primary_link'], color: '#555555', width: [2, 4] },
        { id: 'osm-roads-secondary', filter: ['secondary', 'secondary_link'], color: '#666666', width: [1.5, 3] },
        { id: 'osm-roads-tertiary', filter: ['tertiary', 'tertiary_link'], color: '#777777', width: [1, 2.5] },
        { id: 'osm-roads-residential', filter: ['residential', 'service', 'unclassified'], color: '#888888', width: [0.5, 2] },
        { id: 'osm-roads-track', filter: ['track', 'path', 'footway', 'cycleway'], color: '#999999', width: [0.3, 1] },
      ];

      for (const s of roadStyles) {
        map.addLayer({
          id: s.id,
          type: 'line',
          source: 'osm-portugal',
          'source-layer': 'roads',
          filter: ['in', ['get', 'kind_detail'], ['literal', s.filter]],
          paint: {
            'line-color': s.color,
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, s.width[0], 16, s.width[1]],
            'line-opacity': state.roadsVisible ? state.roadsOpacity : 0,
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        }, insertBefore);
      }

      console.log('OSM Roads context layers added to 2D map');
    };

    // Add OSM buildings context layer (PMTiles, grey fill polygons)
    const addBuildingsContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-buildings-fill')) return;

      // Source already added by addRoadsContextLayers, but add if roads are disabled
      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', {
          type: 'vector',
          url: OSM_PMTILES_URL,
        });
      }

      const state = useAtlasStore.getState();
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') {
          insertBefore = l.id;
          break;
        }
      }

      map.addLayer({
        id: 'osm-buildings-fill',
        type: 'fill',
        source: 'osm-portugal',
        'source-layer': 'buildings',
        filter: ['==', ['get', 'kind'], 'building'],
        paint: {
          'fill-color': '#808080',
          'fill-opacity': state.buildingsVisible ? state.buildingsOpacity : 0,
        },
      }, insertBefore);

      map.addLayer({
        id: 'osm-buildings-outline',
        type: 'line',
        source: 'osm-portugal',
        'source-layer': 'buildings',
        filter: ['==', ['get', 'kind'], 'building'],
        paint: {
          'line-color': '#666666',
          'line-width': 0.5,
          'line-opacity': state.buildingsVisible ? 1 : 0,
        },
      }, insertBefore);

      console.log('OSM Buildings context layers added to 2D map');
    };

    // Add OSM land use context layer (PMTiles, categorized fill: forest/agriculture/urban)
    const addLanduseContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-landuse-fill')) return;

      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', { type: 'vector', url: OSM_PMTILES_URL });
      }

      const state = useAtlasStore.getState();
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') { insertBefore = l.id; break; }
      }

      map.addLayer({
        id: 'osm-landuse-fill',
        type: 'fill',
        source: 'osm-portugal',
        'source-layer': 'landuse',
        filter: ['in', ['get', 'kind'], ['literal', ['forest', 'wood', 'farmland', 'farmyard', 'orchard', 'meadow', 'grass', 'residential', 'commercial', 'industrial']]],
        paint: {
          'fill-color': ['match', ['get', 'kind'],
            'forest', '#5A8A42', 'wood', '#5A8A42',
            'farmland', '#D4C878', 'farmyard', '#D4C878', 'orchard', '#D4C878', 'meadow', '#D4C878', 'grass', '#D4C878',
            'residential', '#A0A0A0', 'commercial', '#A0A0A0', 'industrial', '#A0A0A0',
            '#cccccc',
          ],
          'fill-opacity': state.landuseVisible ? state.landuseOpacity : 0,
        },
      }, insertBefore);

      console.log('OSM Land use context layer added to 2D map');
    };

    // Add OSM waterways context layer (PMTiles, blue lines for rivers/streams/canals)
    const addWaterwaysContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-waterways-line')) return;

      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', { type: 'vector', url: OSM_PMTILES_URL });
      }

      const state = useAtlasStore.getState();
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') { insertBefore = l.id; break; }
      }

      map.addLayer({
        id: 'osm-waterways-line',
        type: 'line',
        source: 'osm-portugal',
        'source-layer': 'water',
        filter: ['in', ['get', 'kind'], ['literal', ['stream', 'river', 'canal']]],
        paint: {
          'line-color': '#6baed6',
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 1.5],
          'line-opacity': state.waterwaysVisible ? state.waterwaysOpacity : 0,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      }, insertBefore);

      console.log('OSM Waterways context layer added to 2D map');
    };

    // Add OSM admin boundaries context layer (PMTiles, dashed grey lines)
    const addAdminBoundsContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-admin-bounds-line')) return;

      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', { type: 'vector', url: OSM_PMTILES_URL });
      }

      const state = useAtlasStore.getState();
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') { insertBefore = l.id; break; }
      }

      map.addLayer({
        id: 'osm-admin-bounds-line',
        type: 'line',
        source: 'osm-portugal',
        'source-layer': 'boundaries',
        paint: {
          'line-color': '#555555',
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 14, 2.5],
          'line-opacity': state.adminBoundsVisible ? state.adminBoundsOpacity : 0,
          'line-dasharray': [6, 4],
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      }, insertBefore);

      console.log('OSM Admin boundaries context layer added to 2D map');
    };

    // Wrap each layer-add in try/catch so one failure doesn't block the rest
    const addAllLayers = () => {
      try { addRoadsContextLayers(); } catch (e) { console.warn('addRoadsContextLayers error:', e); }
      try { addBuildingsContextLayers(); } catch (e) { console.warn('addBuildingsContextLayers error:', e); }
      try { addLanduseContextLayers(); } catch (e) { console.warn('addLanduseContextLayers error:', e); }
      try { addWaterwaysContextLayers(); } catch (e) { console.warn('addWaterwaysContextLayers error:', e); }
      try { addAdminBoundsContextLayers(); } catch (e) { console.warn('addAdminBoundsContextLayers error:', e); }
      try { addMissingLayers(); } catch (e) { console.warn('addMissingLayers error:', e); }
      try { addContourDgtLayers(); } catch (e) { console.warn('addContourDgtLayers error:', e); }
      try { addStreamsLayers(); } catch (e) { console.warn('addStreamsLayers error:', e); }
      try { addRidgesLayers(); } catch (e) { console.warn('addRidgesLayers error:', e); }
      try { addBasinsLayers(); } catch (e) { console.warn('addBasinsLayers error:', e); }
      try { addFloodZonesLayers(); } catch (e) { console.warn('addFloodZonesLayers error:', e); }
      try { addKeypointsLayers(); } catch (e) { console.warn('addKeypointsLayers error:', e); }
      try { addPrimaryValleysLayers(); } catch (e) { console.warn('addPrimaryValleysLayers error:', e); }
      try { addPrimaryRidgesLayers(); } catch (e) { console.warn('addPrimaryRidgesLayers error:', e); }
    };
    map.once('load', addAllLayers);
    const unsub = useAtlasStore.subscribe(addAllLayers);
    // Poll as fallback — subscribe may not fire reliably for late store updates
    const interval = setInterval(addAllLayers, 500);
    const stopPolling = setTimeout(() => clearInterval(interval), 15000);

    return () => {
      unsub();
      clearInterval(interval);
      clearTimeout(stopPolling);
      map.remove();
      mapRef.current = null;
      polygonAddedRef.current = false;
      maplibregl.removeProtocol('pmtiles');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Add polygon when order data is available
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !orderPolygon || polygonAddedRef.current) return;

    const addPolygon = () => {
      if (polygonAddedRef.current) return;

      map.addSource('order-polygon', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: orderPolygon,
          properties: {},
        },
      });

      map.addLayer({
        id: 'order-polygon-fill',
        type: 'fill',
        source: 'order-polygon',
        paint: {
          'fill-color': '#2196F3',
          'fill-opacity': 0.25,
        },
      });

      map.addLayer({
        id: 'order-polygon-outline',
        type: 'line',
        source: 'order-polygon',
        paint: {
          'line-color': '#1565C0',
          'line-width': 3,
        },
      });

      polygonAddedRef.current = true;
    };

    if (map.isStyleLoaded()) {
      addPolygon();
    } else {
      map.once('load', addPolygon);
    }
  }, [orderPolygon]);

  // Fly to bounds on first load
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !orderBounds) return;

    const [west, south, east, north] = orderBounds;

    const flyToBounds = () => {
      map.fitBounds(
        [[west, south], [east, north]],
        { padding: 60, duration: 1500 }
      );
    };

    if (map.isStyleLoaded()) {
      flyToBounds();
    } else {
      map.once('load', flyToBounds);
    }
  }, [orderBounds]);

  // Toggle DGT raster layer visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('mdt-layer')) map.setPaintProperty('mdt-layer', 'raster-opacity', mdtVisible ? mdtOpacity : 0); } catch {} }, [mdtVisible, mdtOpacity]);
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('hillshade-layer')) map.setPaintProperty('hillshade-layer', 'raster-opacity', hillshadeVisible ? hillshadeOpacity : 0); } catch {} }, [hillshadeVisible, hillshadeOpacity]);
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('hillshade-hypso-layer')) map.setPaintProperty('hillshade-hypso-layer', 'raster-opacity', hillshadeHypsoVisible ? hillshadeHypsoOpacity : 0); } catch {} }, [hillshadeHypsoVisible, hillshadeHypsoOpacity]);

  // Toggle slope visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('slope-layer')) map.setPaintProperty('slope-layer', 'raster-opacity', slopeVisible ? slopeOpacity : 0); } catch {} }, [slopeVisible, slopeOpacity]);
  // Toggle aspect visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('aspect-layer')) map.setPaintProperty('aspect-layer', 'raster-opacity', aspectVisible ? aspectOpacity : 0); } catch {} }, [aspectVisible, aspectOpacity]);
  // Toggle geomorphons visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('geomorphons-layer')) map.setPaintProperty('geomorphons-layer', 'raster-opacity', geomorphonsVisible ? geomorphonsOpacity : 0); } catch {} }, [geomorphonsVisible, geomorphonsOpacity]);
  // Toggle wind exposition visibility + opacity (hillshade underlay + exposition overlay)
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('wind-hillshade-layer')) map.setPaintProperty('wind-hillshade-layer', 'raster-opacity', windExpositionVisible ? 1 : 0); } catch {} }, [windExpositionVisible]);
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('wind-exposition-layer')) map.setPaintProperty('wind-exposition-layer', 'raster-opacity', windExpositionVisible ? windExpositionOpacity : 0); } catch {} }, [windExpositionVisible, windExpositionOpacity]);
  // Toggle keypoints visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('keypoints-circle')) {
        map.setPaintProperty('keypoints-circle', 'circle-opacity', keypointsVisible ? keypointsOpacity : 0);
        map.setPaintProperty('keypoints-circle', 'circle-stroke-opacity', keypointsVisible ? keypointsOpacity : 0);
      }
      if (map.getLayer('keypoints-labels')) map.setLayoutProperty('keypoints-labels', 'visibility', keypointsVisible ? 'visible' : 'none');
    } catch {}
  }, [keypointsVisible, keypointsOpacity]);
  // Toggle primary valleys visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('primary-valleys-fill')) map.setPaintProperty('primary-valleys-fill', 'fill-opacity', primaryValleysVisible ? primaryValleysOpacity : 0);
      if (map.getLayer('primary-valleys-outline')) map.setPaintProperty('primary-valleys-outline', 'line-opacity', primaryValleysVisible ? 1 : 0);
    } catch {}
  }, [primaryValleysVisible, primaryValleysOpacity]);
  // Toggle primary ridges visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('primary-ridges-fill')) map.setPaintProperty('primary-ridges-fill', 'fill-opacity', primaryRidgesVisible ? primaryRidgesOpacity : 0);
      if (map.getLayer('primary-ridges-outline')) map.setPaintProperty('primary-ridges-outline', 'line-opacity', primaryRidgesVisible ? 1 : 0);
    } catch {}
  }, [primaryRidgesVisible, primaryRidgesOpacity]);
  // Toggle erosion risk visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('erosion-risk-layer')) map.setPaintProperty('erosion-risk-layer', 'raster-opacity', erosionRiskVisible ? erosionRiskOpacity : 0); } catch {} }, [erosionRiskVisible, erosionRiskOpacity]);
  // Toggle flow accumulation visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('flow-acc-layer')) map.setPaintProperty('flow-acc-layer', 'raster-opacity', flowAccVisible ? flowAccOpacity : 0); } catch {} }, [flowAccVisible, flowAccOpacity]);
  // Toggle streams visibility + opacity (all orders visible for parcel-level analysis)
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      for (const id of ['streams-order-1', 'streams-order-2', 'streams-order-3', 'streams-order-4', 'streams-order-5', 'streams-order-6', 'streams-order-high']) {
        if (map.getLayer(id)) map.setPaintProperty(id, 'line-opacity', streamsVisible ? streamsOpacity : 0);
      }
    } catch {}
  }, [streamsVisible, streamsOpacity]);
  // Toggle ridges visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      for (const id of ['ridges-order-1', 'ridges-order-2', 'ridges-order-3', 'ridges-order-4', 'ridges-order-5', 'ridges-order-6', 'ridges-order-high']) {
        if (map.getLayer(id)) map.setPaintProperty(id, 'line-opacity', ridgesVisible ? ridgesOpacity : 0);
      }
    } catch {}
  }, [ridgesVisible, ridgesOpacity]);
  // Toggle basins visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('basins-fill')) map.setPaintProperty('basins-fill', 'fill-opacity', basinsVisible ? basinsOpacity : 0);
      if (map.getLayer('basins-outline')) map.setPaintProperty('basins-outline', 'line-opacity', basinsVisible ? 1 : 0);
      if (map.getLayer('basins-labels')) map.setLayoutProperty('basins-labels', 'visibility', basinsVisible ? 'visible' : 'none');
    } catch {}
  }, [basinsVisible, basinsOpacity]);
  // Toggle flood zones visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('flood-zones-fill')) map.setPaintProperty('flood-zones-fill', 'fill-opacity', floodZonesVisible ? floodZonesOpacity : 0);
      if (map.getLayer('flood-zones-outline')) map.setPaintProperty('flood-zones-outline', 'line-opacity', floodZonesVisible ? 1 : 0);
      if (map.getLayer('flood-zones-labels')) map.setLayoutProperty('flood-zones-labels', 'visibility', floodZonesVisible ? 'visible' : 'none');
    } catch {}
  }, [floodZonesVisible, floodZonesOpacity]);
  // Toggle OSM roads visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      for (const id of ['osm-roads-motorway', 'osm-roads-trunk', 'osm-roads-primary', 'osm-roads-secondary', 'osm-roads-tertiary', 'osm-roads-residential', 'osm-roads-track']) {
        if (map.getLayer(id)) map.setPaintProperty(id, 'line-opacity', roadsVisible ? roadsOpacity : 0);
      }
    } catch {}
  }, [roadsVisible, roadsOpacity]);
  // Toggle OSM buildings visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('osm-buildings-fill')) map.setPaintProperty('osm-buildings-fill', 'fill-opacity', buildingsVisible ? buildingsOpacity : 0);
      if (map.getLayer('osm-buildings-outline')) map.setPaintProperty('osm-buildings-outline', 'line-opacity', buildingsVisible ? 1 : 0);
    } catch {}
  }, [buildingsVisible, buildingsOpacity]);
  // Toggle OSM land use visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('osm-landuse-fill')) map.setPaintProperty('osm-landuse-fill', 'fill-opacity', landuseVisible ? landuseOpacity : 0);
    } catch {}
  }, [landuseVisible, landuseOpacity]);
  // Toggle OSM waterways visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('osm-waterways-line')) map.setPaintProperty('osm-waterways-line', 'line-opacity', waterwaysVisible ? waterwaysOpacity : 0);
    } catch {}
  }, [waterwaysVisible, waterwaysOpacity]);
  // Toggle OSM admin boundaries visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('osm-admin-bounds-line')) map.setPaintProperty('osm-admin-bounds-line', 'line-opacity', adminBoundsVisible ? adminBoundsOpacity : 0);
    } catch {}
  }, [adminBoundsVisible, adminBoundsOpacity]);

  // Toggle DGT contour visibility + opacity
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (map.getLayer('contours-dgt-5m-layer'))
        map.setPaintProperty('contours-dgt-5m-layer', 'line-opacity', contoursVisible ? contoursOpacity : 0);
      if (map.getLayer('contours-dgt-1m-layer'))
        map.setPaintProperty('contours-dgt-1m-layer', 'line-opacity', contoursVisible ? contoursOpacity : 0);
      if (map.getLayer('contours-dgt-labels-layer')) {
        map.setLayoutProperty('contours-dgt-labels-layer', 'visibility', contoursVisible ? 'visible' : 'none');
      }
    } catch {}
  }, [contoursVisible, contoursOpacity]);

  // Toggle polygon visibility
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !polygonAddedRef.current) return;

    try {
      if (map.getLayer('order-polygon-fill')) {
        map.setLayoutProperty('order-polygon-fill', 'visibility', showPolygon ? 'visible' : 'none');
      }
      if (map.getLayer('order-polygon-outline')) {
        map.setLayoutProperty('order-polygon-outline', 'visibility', showPolygon ? 'visible' : 'none');
      }
    } catch {
      // Layer might not be added yet
    }
  }, [showPolygon]);

  // Switch basemap
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const basemap = BASEMAPS[baseLayer];
    if (!basemap) return;

    const source = map.getSource('basemap') as maplibregl.RasterTileSource;
    if (source) {
      source.setTiles([basemap.url]);
    }
  }, [baseLayer]);

  // When switching to 2D mode, fly to stored camera position
  useEffect(() => {
    const map = mapRef.current;
    if (!map || viewMode !== '2d') return;

    // Resize map after display:none → display:block transition
    setTimeout(() => {
      map.resize();
    }, 50);
  }, [viewMode]);

  // Expose flyToOrder for Toolbar home button
  const flyToOrder = useCallback(() => {
    const map = mapRef.current;
    if (!map || !orderBounds) return;

    const [west, south, east, north] = orderBounds;
    map.fitBounds(
      [[west, south], [east, north]],
      { padding: 60, duration: 1500 }
    );
  }, [orderBounds]);

  useEffect(() => {
    (window as any).__atlas_flyToOrder2D = flyToOrder;
    return () => { delete (window as any).__atlas_flyToOrder2D; };
  }, [flyToOrder]);

  return (
    <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
  );
}
